import { FC } from 'react';
import CheckpointHoleIcon from '../../assets/CheckpointIcon/CheckpointHole';
import TargetSkillIcon from '../../assets/CheckpointIcon/TargetSkillIcon';

interface VisualProgressBarProps {
  /** Total number of skills/items */
  totalItems: number;
  /** Number of completed skills/items */
  completedItems: number;
  /** Whether to show the checkpoint indicator on the right */
  showCheckpointIndicator?: boolean;
  /** Whether target skills are completed (shows TargetSkillIcon instead of CheckpointHoleIcon) */
  targetSkillsCompleted?: boolean;
  /** Custom className for the progress bar container */
  className?: string;
  /** Custom height for the progress bar (default: h-5) */
  height?: string;
}

/**
 * Reusable visual progress bar component that matches UnitCard.tsx styling
 * Used across dashboard components for consistent progress visualization
 */
const VisualProgressBar: FC<VisualProgressBarProps> = ({
  totalItems,
  completedItems,
  showCheckpointIndicator = true,
  targetSkillsCompleted = false,
  className = '',
  height = 'tw-h-5',
}) => {
  const progressPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <div className={`tw-self-stretch tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-3 ${className}`}>
      <div className={`tw-self-stretch ${height} tw-inline-flex tw-justify-start tw-items-start tw-gap-2`}>
        <div
          className={`tw-flex-1 ${height} tw-relative tw-self-stretch tw-pl-1 ${
            targetSkillsCompleted ? 'tw-pr-6' : 'tw-pr-2'
          } tw-py-1 tw-bg-neutral-100 tw-rounded-[1000px] tw-outline tw-outline-1 tw-outline-offset-[-0.50px] tw-outline-neutral-200 tw-inline-flex tw-justify-between tw-items-center ${
            targetSkillsCompleted ? 'tw-overflow-visible' : 'tw-overflow-hidden'
          }`}
        >
          {/* Background progress bar track */}
          <div className="tw-absolute tw-inset-0 tw-bg-neutral-200 tw-rounded-[40px]"></div>

          {/* Filled progress bar */}
          {totalItems > 0 && (
            <div
              className={`tw-absolute tw-left-1 tw-top-0.25 tw-h-3/4 tw-bg-yellow-500 tw-rounded-2xl tw-transition-all tw-duration-300 tw-ease-out tw-overflow-hidden ${
                targetSkillsCompleted ? 'tw-origin-left' : ''
              }`}
              style={{
                width: `${progressPercentage}%`,
                transform: targetSkillsCompleted ? 'scaleX(0.99)' : 'none',
              }}
            ></div>
          )}

          {/* Checkpoint indicator */}
          {showCheckpointIndicator && (
            <div className="">
              {targetSkillsCompleted ? (
                <div className="tw-absolute -tw-right-1 tw-top-3 tw-transform tw--translate-y-8">
                  <TargetSkillIcon />
                </div>
              ) : (
                <div className="tw-absolute tw-right-2 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-4 tw-h-5">
                  <CheckpointHoleIcon />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisualProgressBar;
